import FeedbackRegenerationAction from 'components/Grading/FeedbackRegenerationAction';
import FeedbackRenderer from 'components/Grading/FeedbackRenderer';
import FileSelector from 'components/Grading/FileSelector';
import FileViewer from 'components/Grading/FileViewer';
import GradingRegenerating from 'components/Grading/GradingRegenerating';
import Header from 'components/Grading/Header';
import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import RichTextSubmissionViewer from 'components/Grading/RichTextSubmissionViewer';
import RubricRenderer from 'components/Grading/RubricRenderer';
import { useJoyride } from 'contexts/joyride';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    ButtonGroup,
    Container,
    Dropdown,
    IconButton,
    PDFViewer,
    SheetViewer,
    Stack,
    styled,
    Text,
    VideoViewer,
} from 'shell/components';
import { getS3FileURL, getTooltipComponent } from 'utils/helper';

const Mobile = ({
    currentAssignment,
    currentSubmission,
    selectedCriterionTitle,
    highlights,
    selectedFileId,
    setSelectedFileId,
    form,
    setForm,
    gradeOverrides,
    setGradeOverrides,
    meta,
    tab,
    setTab,
    selectedCriterionId,
    setSelectedCriterionId,
    selectedFeedbackId,
    setSelectedFeedbackId,
    selectedInlineCommentId,
    setSelectedInlineCommentId,
    isGradesGenerating,
    feedbackRegenerationReasons,
    feedbackRegenerationNeededItemIds,
    feedbackUpdatedItemIds,
    pendingFeedbackItemIds,
    setShowFailedDialog,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    handleClickRegenerateFeedback,
    handleRevertFeedback,
    handleSelectDropdown,
    handleCriterionNavigation,
}) => {
    const { joyrideRun, joyrideStepIndex, performNext, performPrev } = useJoyride();
    const { t } = useTranslation('assignment');

    const currentFile = currentSubmission.files.find(({ id }) => id === selectedFileId);
    const { bucket, key, name } = currentFile ?? {};
    const url = useMemo(() => getS3FileURL({ bucket, key, name }), [bucket, key, name]);

    const renderableHighlights = highlights
        .filter(({ value }) => {
            if (typeof value === 'string') return true;
            return value.file_id === selectedFileId;
        })
        .map(({ id, value }) => {
            if (typeof value === 'string') return { id, value };
            return { id: id, index: value.index, value: value.highlight ?? value.quote };
        });

    return (
        <CustomContainer>
            <Header />
            <ContentStack>
                <TopStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isDocumentFullScreen && (
                        <BannerBox>
                            <Text color={'inherit'}>
                                {tab === 'grades' && selectedCriterionId
                                    ? t(
                                          'grading-section.document-preview.criterion-annotations',
                                          `Currently viewing annotations for the criterion "{{criterionTitle}}."`,
                                          {
                                              criterionTitle: selectedCriterionTitle,
                                          }
                                      )
                                    : tab === 'feedback' && selectedFeedbackId
                                      ? t(
                                            'grading-section.document-preview.feedback-annotations',
                                            `Currently viewing annotations for feedback.`
                                        )
                                      : t(
                                            'grading-section.document-preview.inline-comments-annotations',
                                            `Currently viewing annotations for inline comments.`
                                        )}{' '}
                            </Text>
                        </BannerBox>
                    )}
                    {currentFile && (
                        <Box sx={{ height: 'calc(100% - 44px)' }}>
                            {currentFile.type === 'application/pdf' ? (
                                <PDFViewer
                                    url={url}
                                    highlights={renderableHighlights}
                                    inlineComments={form.inline_comments}
                                    setInlineComments={setForm}
                                    selectedInlineCommentId={selectedInlineCommentId}
                                    setSelectedInlineCommentId={setSelectedInlineCommentId}
                                    isFullScreen={isDocumentFullScreen}
                                    setIsFullScreen={setIsDocumentFullScreen}
                                />
                            ) : currentFile.type === 'text/csv' ? (
                                <SheetViewer highlights={renderableHighlights} url={url} />
                            ) : currentFile.type === 'text/html' && currentFile.lms_text_entry ? (
                                <RichTextSubmissionViewer
                                    content={currentFile.content}
                                    highlights={renderableHighlights}
                                    url={url}
                                />
                            ) : currentFile.type.startsWith('video/') ||
                              currentFile.type.startsWith('application/mp4') ? (
                                <VideoViewer
                                    highlights={renderableHighlights}
                                    mimeType={currentFile.type}
                                    transcript={currentFile.transcript}
                                    url={url}
                                />
                            ) : (
                                <FileViewer docUrl={url} fileType={currentFile.type} />
                            )}
                        </Box>
                    )}
                    <FileSelector
                        submission={currentSubmission}
                        selectedFileId={selectedFileId}
                        onClick={(file) => setSelectedFileId(file.id)}
                    />
                </TopStack>
                <BottomStack
                    id={'actions-container'}
                    className={'joyride-step-8 joyride-step-10'}
                    isDocumentFullScreen={isDocumentFullScreen}
                    joyrideRun={joyrideRun}
                >
                    <ControlsStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <ButtonGroup
                            className={'joyride-step-9'}
                            disabled={joyrideStepIndex === 7 || joyrideStepIndex === 9}
                            options={[
                                {
                                    key: 'grades',
                                    value: t('common:ui.grades', 'Grades'),
                                    disabled: Boolean(currentSubmission.is_failed),
                                },
                                {
                                    key: 'feedback',
                                    value: t('common:ui.feedback', 'Feedback'),
                                    badge: !meta.hasSeenFeedback,
                                    disabled: currentSubmission.is_failed ? false : !currentSubmission.ai.feedback,
                                },
                            ]}
                            selected={tab}
                            onChange={(tab) => {
                                setTab(tab);
                                if (joyrideRun) {
                                    if (tab === 'feedback') {
                                        performNext();
                                    } else {
                                        performPrev();
                                    }
                                }
                            }}
                        />
                        <Stack direction={'row'} spacing={3}>
                            {currentSubmission.ai?.feedback?.feedback_type !== 'templated' && (
                                <FeedbackRegenerationAction
                                    aria-label={'see ai options'}
                                    count={Object.keys(feedbackRegenerationReasons).length}
                                    disabled={joyrideRun || isGradesGenerating}
                                    onClick={handleClickRegenerateFeedback}
                                />
                            )}
                            {currentSubmission.ai?.feedback?.feedback_type !== 'templated' && (
                                <Dropdown
                                    id={'grading-more-options-dropdown'}
                                    options={
                                        tab === 'grades'
                                            ? [
                                                  {
                                                      key: 'regenerate-grades',
                                                      value: t(
                                                          'grading-section.actions.regenerate-grades',
                                                          'Regenerate Grades'
                                                      ),
                                                      icon: 'refresh',
                                                      tooltip: getTooltipComponent(
                                                          t(
                                                              'grading-section.actions.regenerate-grades',
                                                              'Regenerate Grades'
                                                          ),
                                                          t(
                                                              'grading-section.regenerate-grades-tooltip',
                                                              'In rare cases, AI grading may fail. You can regenerate grading suggestions, but feedback must be regenerated separately. Each regeneration is independent, so outcomes may vary.'
                                                          )
                                                      ),
                                                  },
                                              ]
                                            : [
                                                  {
                                                      key: 'copy-all',
                                                      value: t(
                                                          'grading-section.actions.copy-all-feedback',
                                                          'Copy all feedback'
                                                      ),
                                                      icon: 'copy',
                                                  },
                                                  {
                                                      key: 'delete',
                                                      value: t(
                                                          'grading-section.actions.delete-all-feedback',
                                                          'Delete all feedback'
                                                      ),
                                                      icon: 'delete',
                                                  },
                                              ]
                                    }
                                    rootComponent={
                                        <IconButton
                                            aria-label={'more options'}
                                            disabled={joyrideRun || (tab === 'grades' && isGradesGenerating)}
                                            kind={'moreHorizontal'}
                                            size={'medium'}
                                            variant={'square'}
                                        />
                                    }
                                    onSelect={handleSelectDropdown}
                                />
                            )}
                        </Stack>
                    </ControlsStack>
                    {tab === 'grades' && (
                        <>
                            {isGradesGenerating ? (
                                <GradingRegenerating />
                            ) : (
                                <RubricRenderer
                                    assignment={currentAssignment}
                                    currentSubmission={currentSubmission}
                                    selectedFileId={selectedFileId}
                                    setSelectedFileId={setSelectedFileId}
                                    locations={highlights}
                                    form={form}
                                    setForm={
                                        joyrideRun
                                            ? (update) => {
                                                  setForm(update);
                                                  performNext();
                                              }
                                            : setForm
                                    }
                                    gradeOverrides={gradeOverrides}
                                    setGradeOverrides={setGradeOverrides}
                                    selectedCriterionId={selectedCriterionId}
                                    setSelectedCriterionId={setSelectedCriterionId}
                                    setShowFailedDialog={setShowFailedDialog}
                                    feedbackRegenerationReasons={feedbackRegenerationReasons}
                                    handleCriterionNavigation={handleCriterionNavigation}
                                />
                            )}
                        </>
                    )}
                    {tab === 'feedback' && (
                        <Stack sx={{ flex: 1, overflow: 'auto' }}>
                            {currentAssignment.ai_feedback_settings?.structure === 'templated' ||
                            typeof currentSubmission.ai?.feedback === 'object' ? (
                                <FeedbackRenderer
                                    assignment={currentAssignment}
                                    currentSubmission={currentSubmission}
                                    selectedFileId={selectedFileId}
                                    setSelectedFileId={setSelectedFileId}
                                    locations={highlights}
                                    form={form}
                                    setForm={setForm}
                                    selectedFeedbackId={selectedFeedbackId}
                                    setSelectedFeedbackId={setSelectedFeedbackId}
                                    pendingFeedbackItemIds={pendingFeedbackItemIds}
                                    feedbackRegenerationNeededItemIds={feedbackRegenerationNeededItemIds}
                                    feedbackUpdatedItemIds={feedbackUpdatedItemIds}
                                    onRevertFeedback={handleRevertFeedback}
                                    handleCriterionNavigation={handleCriterionNavigation}
                                />
                            ) : (
                                <LegacyFeedbackRenderer
                                    currentSubmission={currentSubmission}
                                    form={form}
                                    setForm={setForm}
                                    setShowFailedDialog={setShowFailedDialog}
                                    needsFeedbackRegeneration={feedbackRegenerationNeededItemIds.length > 0}
                                    feedbackUpdatedItemIds={feedbackUpdatedItemIds}
                                    pendingFeedbackItemIds={pendingFeedbackItemIds}
                                    onRevertFeedback={handleRevertFeedback}
                                />
                            )}
                        </Stack>
                    )}
                </BottomStack>
            </ContentStack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        paddingTop: '109px',
        background: theme.palette.ensightful.neutral[100],
    };
});

const ContentStack = styled(Stack)(() => {
    return {
        height: 'calc(100vh - 109px)',
        overflow: 'auto',
    };
});

const TopStack = styled(Stack)(({ isDocumentFullScreen }) => {
    return {
        position: 'relative',
        height: isDocumentFullScreen ? 'calc(100% - 36px - 24px)' : '45%',
        transition: 'height 150ms',
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const BottomStack = styled(Stack)(({ theme, isDocumentFullScreen, joyrideRun }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        height: isDocumentFullScreen ? 0 : 'calc(55% - 61px)',
        background: theme.palette.ensightful.shades[0],
        transition: 'height 150ms',
    };
});

const ControlsStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(3, 5),
    };
});

export default Mobile;
